import React, { useEffect } from "react";
import { Bell, BellOff, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Panel, dateKey, dayHasData } from "./scShared";

export const DEFAULT_REMINDER = { enabled: false, time: "19:30", lastFired: "" };

const nowHM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

// Rappel du soir : notification navigateur (si autorisée) + toast, tant que l'app est ouverte.
export const useEveningReminder = (state, update, onOpenMission) => {
  useEffect(() => {
    const r = { ...DEFAULT_REMINDER, ...(state.reminder || {}) };
    if (!r.enabled) return undefined;
    const check = () => {
      const today = dateKey();
      if (r.lastFired === today || nowHM() < r.time || dayHasData(state.days[today])) return;
      const title = "Mission « Super-Cerveau »";
      const body = `${state.setup.childName ? state.setup.childName + ", " : ""}c'est l'heure de la mission du soir ! 3 à 5 minutes pour trouver ton super-pouvoir du jour.`;
      if ("Notification" in window && Notification.permission === "granted") {
        try { const n = new Notification(title, { body, icon: "/modules/super-cerveau/hero.jpg" }); n.onclick = () => { window.focus(); onOpenMission(); }; } catch { /* ignore */ }
      }
      toast(title, { description: body, duration: 15000, action: { label: "Ouvrir", onClick: onOpenMission } });
      update((s) => ({ ...s, reminder: { ...DEFAULT_REMINDER, ...(s.reminder || {}), lastFired: today } }));
    };
    check();
    const id = setInterval(check, 30000);
    return () => clearInterval(id);
  }, [state.reminder, state.days, state.setup.childName, update, onOpenMission]);
};

const icsDate = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
const downloadIcs = (time, childName) => {
  const [h, m] = time.split(":").map(Number);
  const start = new Date(); start.setHours(h, m, 0, 0);
  if (start < new Date()) start.setDate(start.getDate() + 1);
  const end = new Date(start.getTime() + 10 * 60000);
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//M2HAT Psychologie//Super-Cerveau//FR", "BEGIN:VEVENT", `UID:super-cerveau-${Date.now()}@m2hat`, `DTSTAMP:${icsDate(new Date())}`, `DTSTART:${icsDate(start)}`, `DTEND:${icsDate(end)}`, "RRULE:FREQ=DAILY", `SUMMARY:Mission « Super-Cerveau »${childName ? " — " + childName : ""}`, "DESCRIPTION:Rituel du soir (3 à 5 min) : jauge d'énergie, super-pouvoir, obstacle, montagne infranchissable, astuce de demain.", "BEGIN:VALARM", "TRIGGER:-PT0M", "ACTION:DISPLAY", "DESCRIPTION:Mission du soir", "END:VALARM", "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar;charset=utf-8" }));
  const a = document.createElement("a"); a.href = url; a.download = "mission-super-cerveau-rappel.ics"; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function ScRappel({ state, update }) {
  const r = { ...DEFAULT_REMINDER, ...(state.reminder || {}) };
  const setR = (patch) => update((s) => ({ ...s, reminder: { ...DEFAULT_REMINDER, ...(s.reminder || {}), ...patch } }));
  const supported = "Notification" in window;
  const perm = supported ? Notification.permission : "unsupported";

  const toggle = async () => {
    if (r.enabled) { setR({ enabled: false }); toast("Rappel du soir désactivé"); return; }
    if (supported && Notification.permission === "default") {
      try { await Notification.requestPermission(); } catch { /* ignore */ }
    }
    setR({ enabled: true, lastFired: "" });
    toast.success(`Rappel du soir activé à ${r.time}`);
  };

  return (
    <Panel title="Rappel du soir (optionnel)" sub="Une petite notification à l'heure choisie pour ne pas oublier la mission du jour. Elle n'est envoyée que si la mission du jour n'a pas encore été faite." color="mint" testid="sc-rappel-soir">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={toggle} data-testid="sc-reminder-toggle"
          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${r.enabled ? "bg-[#7CC6A6] text-white" : "bg-white border-2 border-[#7CC6A6] text-[#2F7A55]"}`}>
          {r.enabled ? <Bell size={16} /> : <BellOff size={16} />} {r.enabled ? "Rappel activé" : "Activer le rappel"}
        </button>
        <label className="inline-flex items-center gap-2 text-sm text-[var(--ink)]">
          Heure
          <input type="time" value={r.time} onChange={(e) => setR({ time: e.target.value, lastFired: "" })} data-testid="sc-reminder-time"
            className="rounded-full border border-[var(--border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7CC6A6]" />
        </label>
        <button onClick={() => downloadIcs(r.time, state.setup.childName)} data-testid="sc-reminder-ics"
          className="inline-flex items-center gap-2 text-sm text-[var(--gold)] hover:underline"><CalendarPlus size={16} /> Ajouter au calendrier du téléphone (.ics)</button>
      </div>
      <p className="mt-3 text-xs text-[var(--ink-soft)]" data-testid="sc-reminder-status">
        {perm === "unsupported" && "Ce navigateur ne gère pas les notifications : le rappel s'affichera dans l'application si elle est ouverte. Utilisez le calendrier pour un rappel sur le téléphone."}
        {perm === "granted" && "Notifications autorisées. La notification s'affiche si cette page est ouverte dans le navigateur ; pour un rappel même app fermée, ajoutez l'événement au calendrier."}
        {perm === "denied" && "Notifications bloquées dans le navigateur : le rappel s'affichera dans l'application si elle est ouverte. Utilisez le calendrier pour un rappel sur le téléphone."}
        {perm === "default" && "En activant le rappel, le navigateur demandera l'autorisation d'afficher des notifications."}
      </p>
    </Panel>
  );
}
